import { cn } from "@/lib/utils";

type MarkProps = { className?: string };

/**
 * Vendor marks. Path data comes from Simple Icons (icon data is CC0); the trademarks stay
 * with their owners and appear here only to name the client each set of steps targets.
 * Marks whose brand colour is black render in currentColor so they survive dark mode.
 */
const brandMark = (d: string, tone = "text-foreground") => {
  const Mark = ({ className }: MarkProps) => (
    <svg
      viewBox="0 0 24 24"
      className={cn(tone, className)}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d={d} />
    </svg>
  );
  Mark.displayName = "BrandMark";

  return Mark;
};

export const ClaudeMark = brandMark(
  "m4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z",
  "text-[#D97757]",
);

export const ClaudeCodeMark = brandMark(
  "M21 10.5h3v3h-3v3h-1.5v3H18v-3h-1.5v3H15v-3H9v3H7.5v-3H6v3H4.5v-3H3v-3H0v-3h3v-6h18Zm-15 0h1.5v-3H6Zm10.5 0H18v-3h-1.5z",
  "text-[#D97757]",
);

export const ChatGptMark = brandMark(
  "M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z",
);

export const GeminiMark = brandMark(
  "M11.04 19.32Q12 21.51 12 24q0-2.49.93-4.68.96-2.19 2.58-3.81t3.81-2.55Q21.51 12 24 12q-2.49 0-4.68-.93a12.3 12.3 0 0 1-3.81-2.58 12.3 12.3 0 0 1-2.58-3.81Q12 2.49 12 0q0 2.49-.96 4.68-.93 2.19-2.55 3.81a12.3 12.3 0 0 1-3.81 2.58Q2.49 12 0 12q2.49 0 4.68.96 2.19.93 3.81 2.55t2.55 3.81",
  "text-[#8E75B2]",
);

export const CursorMark = brandMark(
  "M11.503.131 1.891 5.678a.84.84 0 0 0-.42.726v11.188c0 .3.162.575.42.724l9.609 5.55a1 1 0 0 0 .998 0l9.61-5.55a.84.84 0 0 0 .42-.724V6.404a.84.84 0 0 0-.42-.726L12.497.131a1.01 1.01 0 0 0-.996 0M2.657 6.338h18.55c.263 0 .43.287.297.515L12.23 22.918c-.062.107-.229.064-.229-.06V12.335a.59.59 0 0 0-.295-.51l-9.11-5.257c-.109-.063-.064-.23.061-.23",
);

export const VsCodeMark = brandMark(
  "M23.15 2.587L18.21.21a1.494 1.494 0 0 0-1.705.29l-9.46 8.63-4.12-3.128a.999.999 0 0 0-1.276.057L.327 7.261A1 1 0 0 0 .326 8.74L3.899 12 .326 15.26a1 1 0 0 0 .001 1.479L1.65 17.94a.999.999 0 0 0 1.276.057l4.12-3.128 9.46 8.63a1.492 1.492 0 0 0 1.704.29l4.942-2.377A1.5 1.5 0 0 0 24 20.06V3.939a1.5 1.5 0 0 0-.85-1.352zm-5.146 14.861L10.826 12l7.178-5.448v10.896z",
  "text-[#0098FF]",
);

export const McpMark = brandMark(
  "M13.85 0a4.16 4.16 0 0 0-2.95 1.217L1.456 10.66a.835.835 0 0 0 0 1.18.835.835 0 0 0 1.18 0l9.442-9.442a2.49 2.49 0 0 1 3.541 0 2.49 2.49 0 0 1 0 3.541L8.59 12.97l-.1.1a.835.835 0 0 0 0 1.18.835.835 0 0 0 1.18 0l.1-.098 7.03-7.034a2.49 2.49 0 0 1 3.542 0l.049.05a2.49 2.49 0 0 1 0 3.54l-8.54 8.54a1.96 1.96 0 0 0 0 2.755l1.753 1.753a.835.835 0 0 0 1.18 0 .835.835 0 0 0 0-1.18l-1.753-1.753a.266.266 0 0 1 0-.394l8.54-8.54a4.185 4.185 0 0 0 0-5.9l-.05-.05a4.16 4.16 0 0 0-2.95-1.218c-.2 0-.401.02-.6.048a4.17 4.17 0 0 0-1.17-3.552A4.16 4.16 0 0 0 13.85 0m0 3.333a.84.84 0 0 0-.59.245L6.275 10.56a4.186 4.186 0 0 0 0 5.902 4.186 4.186 0 0 0 5.902 0L19.16 9.48a.835.835 0 0 0 0-1.18.835.835 0 0 0-1.18 0l-6.985 6.984a2.49 2.49 0 0 1-3.54 0 2.49 2.49 0 0 1 0-3.54l6.983-6.985a.835.835 0 0 0 0-1.18.84.84 0 0 0-.59-.245",
  "text-muted-foreground",
);

/** Simple Icons has no Codex slug, so this one is ours: a terminal prompt in a rounded frame. */
export const CodexMark = ({ className }: MarkProps) => (
  <svg
    viewBox="0 0 24 24"
    className={cn("text-foreground", className)}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <rect x="2.5" y="4" width="19" height="16" rx="3.5" />
    <path d="m7.8 10 2.6 2.2-2.6 2.2" />
    <path d="M13 14.6h3.4" />
  </svg>
);

export interface ConnectContext {
  mcpUrl: string;
  connectorId: string;
  customerName?: string;
}

export interface ConnectStep {
  text: string;
  /** Grey sub-line under the step: the caveat or warning that stops people getting stuck. */
  note?: string;
}

export interface ConnectField {
  label: string;
  value: string;
  /** Monospace and wrapped anywhere. For URLs and ids, not prose. */
  mono?: boolean;
}

export interface AgentApp {
  id: string;
  name: string;
  Mark: (props: MarkProps) => React.JSX.Element;
  steps: (ctx: ConnectContext) => ConnectStep[];
  /** Values the user pastes into the client's own form, each with its own copy button. */
  fields?: (ctx: ConnectContext) => ConnectField[];
  /** For clients configured from a terminal or a file, rendered as a code block. */
  snippet?: (ctx: ConnectContext) => string;
  /** Where the snippet goes, shown above the code block. */
  snippetLabel?: string;
  /** Matched against the OAuth client name to pick a logo for a live grant. */
  match: RegExp;
}

/**
 * Names the connector after its customer, so several are distinguishable in a client's list.
 * Claude Code rejects anything outside [A-Za-z0-9_-] ("Names can only contain letters,
 * numbers, hyphens, and underscores"), which rules out spaces and punctuation. Always starts
 * with "pylo", so it can never be read as a shell flag.
 *
 * Must stay byte-identical to serverName() in backend/mcp-service/src/instructions.ts: this
 * tells the user what to call the connector, that is what the server calls itself, and a
 * drift reads as two different servers. The expected pairs are pinned in that package's
 * instructions.test.ts; this app has no test runner, so change both together.
 */
export const connectorId = (customerName?: string): string => {
  const slug = (customerName ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/, "");

  return slug === "" ? "pylo" : `pylo-${slug}`;
};

const oauthNote =
  "Pylo implements OAuth 2.1 with dynamic client registration, so the client needs no ID or secret from you.";

export const AGENT_APPS: AgentApp[] = [
  {
    id: "claude",
    name: "Claude",
    Mark: ClaudeMark,
    match: /claude(?!\s*code)|claude\.ai/i,
    steps: () => [
      { text: "Open Claude, then Settings → Connectors." },
      { text: "Choose Add custom connector." },
      {
        text: "Paste these fields.",
        note: "Leave OAuth Client ID, OAuth Client Secret and everything else blank.",
      },
      {
        text: "Enable the connector and approve Pylo in the window Claude opens.",
        note: "Claude only offers Pylo's tools once the connector is enabled in a chat.",
      },
    ],
    fields: ({ mcpUrl, connectorId }) => [
      { label: "Name", value: connectorId },
      { label: "Remote MCP server URL", value: mcpUrl, mono: true },
    ],
  },
  {
    id: "claude-code",
    name: "Claude Code",
    Mark: ClaudeCodeMark,
    match: /claude\s*code/i,
    snippetLabel: "Run in your terminal",
    snippet: ({ mcpUrl, connectorId }) =>
      `claude mcp add --transport http ${connectorId} ${mcpUrl}`,
    steps: () => [
      {
        text: "Run the command below in your project directory.",
        note: "Add --scope user to reach Pylo from every project instead of this one.",
      },
      {
        text: "Run /mcp inside Claude Code and authenticate.",
        note: "Claude Code opens your browser for the Pylo consent screen.",
      },
    ],
  },
  {
    id: "chatgpt",
    name: "ChatGPT",
    Mark: ChatGptMark,
    match: /chat\s*gpt|openai/i,
    steps: () => [
      {
        text: "Open ChatGPT, then Settings → Apps & Connectors.",
        note: "If Create is hidden, turn on Developer mode under Advanced settings.",
      },
      { text: "Choose Create." },
      { text: "Paste these fields.", note: "Keep Authentication set to OAuth." },
      {
        text: "Read the warning, tick the box, then create the connector.",
        note: "ChatGPT warns about every custom connector because it cannot vet them. Pylo never writes without your approval.",
      },
    ],
    fields: ({ mcpUrl, connectorId, customerName }) => [
      { label: "Name", value: connectorId },
      {
        label: "Description",
        value: `Read ${customerName ?? "Pylo"} business data and propose changes for your approval: entities, records and aggregates.`,
      },
      { label: "MCP Server URL", value: mcpUrl, mono: true },
      { label: "Authentication", value: "OAuth" },
    ],
  },
  {
    id: "codex",
    name: "Codex",
    Mark: CodexMark,
    match: /codex/i,
    snippetLabel: "Run both in your terminal",
    snippet: ({ mcpUrl, connectorId }) =>
      [
        `codex mcp add ${connectorId} --url ${mcpUrl} --oauth-resource ${mcpUrl}`,
        `codex mcp login ${connectorId}`,
      ].join("\n"),
    steps: () => [
      { text: "Run both commands below." },
      { text: "Approve Pylo in the browser window Codex opens." },
    ],
  },
  {
    id: "cursor",
    name: "Cursor",
    Mark: CursorMark,
    match: /cursor/i,
    snippetLabel: ".cursor/mcp.json",
    snippet: ({ mcpUrl, connectorId }) =>
      JSON.stringify({ mcpServers: { [connectorId]: { url: mcpUrl } } }, null, 2),
    steps: () => [
      {
        text: "Create or edit .cursor/mcp.json in your project.",
        note: "Use ~/.cursor/mcp.json to reach Pylo from every project instead of this one.",
      },
      { text: "Open Settings → MCP and sign in to Pylo on the new server." },
    ],
  },
  {
    id: "vscode",
    name: "VS Code",
    Mark: VsCodeMark,
    match: /vs\s*code|visual studio|copilot/i,
    snippetLabel: ".vscode/mcp.json",
    snippet: ({ mcpUrl, connectorId }) =>
      JSON.stringify({ servers: { [connectorId]: { type: "http", url: mcpUrl } } }, null, 2),
    steps: () => [
      { text: "Create or edit .vscode/mcp.json in your workspace." },
      {
        text: "Run MCP: List Servers from the command palette, start Pylo, then sign in.",
        note: "Pylo's tools show up in Copilot Chat's agent mode, not in ask mode.",
      },
    ],
  },
  {
    id: "gemini",
    name: "Gemini",
    Mark: GeminiMark,
    match: /gemini|google/i,
    snippetLabel: "~/.gemini/settings.json",
    snippet: ({ mcpUrl, connectorId }) =>
      JSON.stringify({ mcpServers: { [connectorId]: { httpUrl: mcpUrl } } }, null, 2),
    steps: () => [
      { text: "Add the server to ~/.gemini/settings.json." },
      { text: "Restart the Gemini CLI, then run /mcp auth to approve Pylo." },
    ],
  },
  {
    id: "other",
    name: "Other MCP client",
    Mark: McpMark,
    match: /.^/,
    steps: () => [
      { text: "Add a remote MCP server over streamable HTTP with the values below." },
      { text: "Approve Pylo when the client opens the OAuth flow.", note: oauthNote },
    ],
    fields: ({ mcpUrl, connectorId }) => [
      { label: "Name", value: connectorId },
      { label: "Server URL", value: mcpUrl, mono: true },
      { label: "Transport", value: "Streamable HTTP" },
      { label: "Authentication", value: "OAuth 2.1" },
    ],
  },
];

const STACK_SIZES = {
  sm: { circle: "h-[18px] w-[18px]", mark: "h-2.5 w-2.5", overlap: "-ml-1.5" },
  md: { circle: "h-9 w-9 shadow-sm", mark: "h-5 w-5", overlap: "-ml-2.5" },
};

/**
 * Overlapping marks. Each one needs its own opaque disc: the vendor glyphs are open shapes, so
 * without the disc a mark behind reads as noise inside the one in front.
 */
export const MarkStack = ({
  apps,
  size = "md",
  className,
}: {
  apps: AgentApp[];
  size?: keyof typeof STACK_SIZES;
  className?: string;
}) => {
  const { circle, mark, overlap } = STACK_SIZES[size];

  return (
    <span className={cn("flex shrink-0 items-center", className)}>
      {apps.map(({ id, Mark }, index) => (
        <span
          key={id}
          className={cn(
            "flex items-center justify-center rounded-full border bg-background",
            circle,
            index > 0 && overlap,
          )}
          style={{ zIndex: apps.length - index }}
        >
          <Mark className={mark} />
        </span>
      ))}
    </span>
  );
};

/** The marks that stand for "AI app" wherever navigation needs a hint rather than a full set. */
export const NAV_MARK_APPS = AGENT_APPS.filter(({ id }) =>
  ["claude", "chatgpt", "gemini"].includes(id),
);

/** The generic mark stands in for a grant whose client name matches none of the known apps. */
export const markForClient = (clientName: string) =>
  AGENT_APPS.find((app) => app.match.test(clientName))?.Mark ?? McpMark;

/**
 * The whole setup as plain text, for the admin who has to hand it to whoever runs the client.
 * Arrows become ">" so the result survives a terminal or a plain-text mail body.
 */
export const instructionsText = (app: AgentApp, ctx: ConnectContext, docsUrl: string): string => {
  const lines = [`Connect Pylo to ${app.name}`, ""];

  app.steps(ctx).forEach((step, index) => {
    lines.push(`${index + 1}. ${step.text.replace(/→/g, ">")}`);
    if (step.note) lines.push(`   ${step.note}`);
  });

  if (app.snippet) {
    lines.push("", `${app.snippetLabel}:`, app.snippet(ctx));
  }

  const fields = app.fields?.(ctx);
  if (fields?.length) {
    lines.push("");
    fields.forEach(({ label, value }) => lines.push(`${label}: ${value}`));
  }

  lines.push(
    "",
    "Every write it proposes waits for your approval. Access can be revoked any time under Settings > Connect Pylo.",
    `Docs: ${docsUrl}`,
  );

  return lines.join("\n");
};
